// ts-rest contract for the public client application (SEP-263, slice 5).
//
// This is the FIRST unauthenticated write surface of the platform. A visitor of
// the Dowgate website starts an application here (SEP-263.1), names the
// investment manager who will receive it (SEP-263.3, SEP-263.5), chooses the
// service type (SEP-263.43) and creates the account that the journey lives
// behind (SEP-263.6).
//
// # THE APPLICANT IS NOT A PortalUser
//
// A `PortalUser` actor carries `{ kind: 'PortalUser', clientId }`, and no
// `Client` row exists until the application is submitted. The applicant account
// therefore lives on the pre-actor typed-Drizzle path, which is the carve-out
// ADR-0007 already sanctions for `client_portal_credentials`. Three firm
// infrastructure tables hold it: `client_applications`,
// `client_application_credentials` and `client_application_sessions`.
//
// NOTHING AN APPLICANT DOES TOUCHES FIRM STATE. No `Client`, no enquiry, no
// portal credential and no document. Slice 7 creates all of those, in one
// atomic act, after Credas verifies the person (decision D10).
//
// # THERE IS NO EMAIL VERIFICATION
//
// The firm settled this on 2026-09-21 (decision D8, SEP-263.48). Credas
// verifies identity by document and liveness at the END of the journey, which
// is stronger than an email code. An email code would be ceremony.
//
// # THE ROSTER CARRIES NO USER ID
//
// `GET /api/apply/investment-managers` is public, so it publishes an OPAQUE
// selector for each manager and the server maps the selector back to the
// `users.id`. The firm directory (`GET /api/users`) stays staff-only: it holds
// the whole Entra allowlist, and no public route may weaken it.
//
// # THE POSTCODE LOOKUP IS THE SAME DOOR (decision D7)
//
// `addressAvailability` and `addressLookup` reuse the schemas and the SERVICE
// that SEP-257 opened for the Stage 3 fact find. They exist under `/apply`
// because the staff route is persona-gated and an applicant holds no persona;
// they open no second vendor door, they hold no key, and a deployment with no
// key refuses with 503 and invents no address.
//
// Endpoints under /api/apply/*:
//   GET  /api/apply/investment-managers  - the dropdown roster
//   POST /api/apply/start                - create the account and the draft
//   POST /api/apply/session              - sign in
//   GET  /api/apply/session              - the application behind the cookie
//   POST /api/apply/sign-out             - end the session
//   GET  /api/apply/draft                - the draft, and where to resume
//   POST /api/apply/draft                - merge answers into the draft
//   GET  /api/apply/address/availability - can this deployment look a postcode up?
//   GET  /api/apply/address/lookup       - the addresses at one postcode
//   GET  /api/apply/verification         - where the identity check stands
//   POST /api/apply/verification         - start the check, and give the link
//   POST /api/apply/submit               - the one atomic submission
//   GET  /api/apply/documents            - the documents to read and acknowledge
//   POST /api/apply/documents/:id/open   - record the open of one document
//   POST /api/apply/documents/:id/acknowledge - record the acknowledgement
//   GET  /api/apply/documents/:id/content - the file (a plain route, not here)

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { clientApplicationQuestion } from '../client-application-questions';
import { portalAcknowledgementStatementCodeSchema } from '../portal-document-acknowledgement';
import { CLIENT_APPLICATION_FORM_IDS } from '../client-application-forms';
import type { ClientApplicationAnswer } from '../client-application-validation';
import {
    addressLookupAvailabilitySchema,
    addressLookupResponseSchema,
    addressLookupResultSchema,
} from './address-lookup';

const c = initContract();

const errorBody = z.object({ error: z.string(), code: z.string().optional() });

// ---------------------------------------------------------------------------
// The service type (SEP-263.43)
// ---------------------------------------------------------------------------

/**
 * The three services the application form offers on page 2.
 *
 * The values are the same as the values of `packServiceTypeSchema`, which the
 * document pack of SEP-256 reads, so a submission needs no mapping.
 */
export const clientApplicationServiceTypeSchema = z.enum([
    'discretionary',
    'advisory',
    'execution_only',
]);
export type ClientApplicationServiceType = z.infer<typeof clientApplicationServiceTypeSchema>;

/**
 * One service-type choice, with the wording of the form.
 *
 * THE COPY IS NOT RETYPED HERE. The label and the note come from the question
 * bank, which holds the firm's own words verbatim (decision D4). The bank
 * prints the options in the order of the form: the discretionary service, the
 * advisory service, then the execution only service.
 */
export interface ClientApplicationServiceTypeOption {
    readonly value: ClientApplicationServiceType;
    readonly label: string;
    readonly note: string;
}

function serviceTypeOptions(): readonly ClientApplicationServiceTypeOption[] {
    const question = clientApplicationQuestion('service_type');
    const labels = question?.options ?? [];
    const notes = question?.optionNotes ?? {};
    const values: readonly ClientApplicationServiceType[] = [
        'discretionary',
        'advisory',
        'execution_only',
    ];
    return labels.map((label, i) => ({
        value: values[i] ?? 'discretionary',
        label,
        note: notes[label] ?? '',
    }));
}

/** The service-type choices, in the order the form prints them. */
export const CLIENT_APPLICATION_SERVICE_TYPE_OPTIONS: readonly ClientApplicationServiceTypeOption[] =
    serviceTypeOptions();

// ---------------------------------------------------------------------------
// The roster
// ---------------------------------------------------------------------------

/**
 * One investment manager, as the public dropdown draws them.
 *
 * The page needs a value and a label, so the projection holds a value and a
 * label. `selector` is an opaque public handle that the server mints from the
 * `users.id`, and a reader cannot turn it back into the id. A test pins that
 * the projection carries no id.
 */
export const applyInvestmentManagerSchema = z.object({
    /** The opaque public handle of this manager. It is never a `users.id`. */
    selector: z.string(),
    /** The display name of the manager, as the firm directory holds it. */
    name: z.string(),
});
export type ApplyInvestmentManager = z.infer<typeof applyInvestmentManagerSchema>;

export const applyInvestmentManagersResponseSchema = z.object({
    managers: z.array(applyInvestmentManagerSchema),
});
export type ApplyInvestmentManagersResponse = z.infer<typeof applyInvestmentManagersResponseSchema>;

// ---------------------------------------------------------------------------
// The application
// ---------------------------------------------------------------------------

export const clientApplicationStatusSchema = z.enum(['draft', 'submitted']);
export type ClientApplicationStatus = z.infer<typeof clientApplicationStatusSchema>;

/**
 * The application as the applicant sees it.
 *
 * It names the manager by `selector` and by name, and never by `users.id`. The
 * answers are not here: slice 6 builds the journey that reads and writes them.
 */
export const clientApplicationSummarySchema = z.object({
    id: z.string(),
    email: z.string(),
    status: clientApplicationStatusSchema,
    /**
     * The three choices below are NULL or empty until the client answers them
     * in the form. The start page collects an email address and a password and
     * nothing else, so a new application names no service and no manager yet.
     * The row still holds them, because the row is what routes the enquiry,
     * and a save of the form writes each answer onto it.
     */
    serviceType: clientApplicationServiceTypeSchema.nullable(),
    investmentManagerSelector: z.string(),
    investmentManagerName: z.string(),
    startedAt: z.string(),
    lastActivityAt: z.string(),
    submittedAt: z.string().nullable(),
});
export type ClientApplicationSummary = z.infer<typeof clientApplicationSummarySchema>;

/**
 * The password an applicant chooses.
 *
 * ADR-0007 states that a portal password comes from a `$`-free alphabet, which
 * a client-chosen password cannot hold to. Only a bcrypt hash is stored, so
 * nothing depends on the alphabet. The floor of 12 characters matches the
 * length of a password that staff mint.
 */
export const applyPasswordSchema = z.string().min(12).max(128);

/**
 * What a visitor gives to open an application.
 *
 * AN ACCOUNT, AND NOTHING ELSE. The service type and the proposed investment
 * manager are questions of the form, and the form is where a client reads the
 * note against each option and changes their mind until they submit.
 */
export const applyStartRequestSchema = z.object({
    email: z.string().email().max(254),
    password: applyPasswordSchema,
});
export type ApplyStartRequest = z.infer<typeof applyStartRequestSchema>;

/**
 * The token of an invitation to the form (SEP-289). The IM or the EA sends the
 * client a link that holds it, and the link binds the application to their
 * case. The token is a secret, so it travels in a body and never in a path.
 */
export const applyInvitationTokenSchema = z.string().min(16).max(128);

export const applyInvitationDescribeRequestSchema = z.object({
    token: applyInvitationTokenSchema,
});

/** What the page shows before the client chooses a password. */
export const applyInvitationResponseSchema = z.object({
    email: z.string(),
    forenames: z.string(),
});
export type ApplyInvitationResponse = z.infer<typeof applyInvitationResponseSchema>;

/**
 * The client accepts an invitation. The email address comes from the
 * invitation, so the request holds the password alone.
 */
export const applyAcceptInvitationRequestSchema = z.object({
    token: applyInvitationTokenSchema,
    password: applyPasswordSchema,
});
export type ApplyAcceptInvitationRequest = z.infer<typeof applyAcceptInvitationRequestSchema>;

export const applySignInRequestSchema = z.object({
    email: z.string().email().max(254),
    password: z.string().min(1).max(128),
});
export type ApplySignInRequest = z.infer<typeof applySignInRequestSchema>;

export const clientApplicationSessionResponseSchema = z.object({
    application: clientApplicationSummarySchema.nullable(),
});
export type ClientApplicationSessionResponse = z.infer<
    typeof clientApplicationSessionResponseSchema
>;

// ---------------------------------------------------------------------------
// The draft (SEP-263, slice 6)
// ---------------------------------------------------------------------------

/**
 * One stored answer, on the wire.
 *
 * It mirrors `ClientApplicationAnswer` of `client-application-validation.ts`: a
 * `multi_select` sends an array of option labels, an `acknowledgement` sends a
 * boolean, a `number` and a `currency` send a NUMBER, and every other kind
 * sends a string. A money answer therefore crosses the wire as the amount and
 * never as the text a person typed, so no reader has to strip a comma.
 */
export const clientApplicationAnswerSchema: z.ZodType<ClientApplicationAnswer> = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.string()),
    z.null(),
]);

/** The answers of one application, keyed by question id. */
export const clientApplicationAnswersSchema = z.record(z.string(), clientApplicationAnswerSchema);

/** One refusal, placed under the field that caused it. */
export const clientApplicationFieldErrorSchema = z.object({
    sectionId: z.string(),
    questionId: z.string(),
    message: z.string(),
});

/** How one section stands, for the progress indication. */
export const clientApplicationSectionProgressSchema = z.object({
    sectionId: z.string(),
    title: z.string(),
    part: z.number().nullable(),
    visibleCount: z.number(),
    answeredCount: z.number(),
    missingRequired: z.array(z.string()),
    complete: z.boolean(),
});

/**
 * One incremental save.
 *
 * The save is a MERGE and not a replacement: the payload holds the answers the
 * client changed, and every other stored answer stays. A cleared field sends
 * `null`, which removes the answer. The same payload sent twice leaves the same
 * draft, so a retry is safe.
 *
 * The request names NO application id. The applicant cookie resolves the one
 * draft the client owns, which is the whole of the ownership rule.
 */
export const applySaveDraftRequestSchema = z.object({
    answers: clientApplicationAnswersSchema,
});
export type ApplySaveDraftRequest = z.infer<typeof applySaveDraftRequestSchema>;

/** A save the server refused. The draft is unchanged, and each field says why. */
export const applyDraftRefusalSchema = z.object({
    error: z.string(),
    code: z.string().optional(),
    fieldErrors: z.array(clientApplicationFieldErrorSchema),
});

// ---------------------------------------------------------------------------
// The identity check, and the submission (SEP-263, slice 7)
// ---------------------------------------------------------------------------

/**
 * `ProcessStatus` of the Credas OpenAPI document, by its own names.
 *
 * The firm stores the WORD and not the number, because a number in a column
 * tells a reader nothing. `fixture` is not a Credas value: it is the answer of
 * the scenario sandbox, and it names itself so that nobody mistakes a sandbox
 * run for a real verdict (decision D8).
 */
export const clientApplicationVerificationStatusSchema = z.enum([
    'not_started',
    'in_progress',
    'complete',
    'deleted',
    'pending_approval',
    'rejected',
    'expired',
    'archived',
    'fixture',
]);
export type ClientApplicationVerificationStatus = z.infer<
    typeof clientApplicationVerificationStatusSchema
>;

/**
 * Where the identity check of one application stands.
 *
 * IT CARRIES NO KYC DATA. The firm stores the pointer and the verdict alone
 * (SEP-263.47): no image, no selfie and no extracted document field, ever. The
 * two vendor ids are here because the applicant page has to name the check it
 * is waiting on, and a reader cannot turn either one into a document.
 */
export const clientApplicationVerificationSchema = z.object({
    /**
     * Whether this deployment can run an identity check at all. False means no
     * key, no host or no journey id, and the form then offers no check. The
     * client can still submit (SEP-278). THE PLATFORM INVENTS NO VERDICT
     * (decision D8, ADR-0023).
     */
    configured: z.boolean(),
    /** The provider that serves the check, or null when none is configured. */
    provider: z.string().nullable(),
    status: clientApplicationVerificationStatusSchema,
    /** The link the applicant opens to complete the check on the vendor site. */
    magicLinkUrl: z.string().nullable(),
    startedAt: z.string().nullable(),
    completedAt: z.string().nullable(),
    /** The instant the firm last read the state from the vendor. */
    checkedAt: z.string().nullable(),
    /** True when the check returned Complete. It does not gate the submission (SEP-278). */
    verified: z.boolean(),
});
export type ClientApplicationVerification = z.infer<typeof clientApplicationVerificationSchema>;

/**
 * What the client is told after they submit (SEP-263.19, SEP-263.23).
 *
 * The case reference is the human handle of the enquiry, and it is what the
 * acknowledgement quotes back. The portal path is where the same email address
 * and password now sign in (decision D2). `verification` is the identity check
 * after the submission (SEP-278): when the server started it, it holds the
 * link that the client opens.
 */
export const clientApplicationSubmitResponseSchema = z.object({
    applicationId: z.string(),
    submissionId: z.string(),
    caseRef: z.string(),
    submittedAt: z.string(),
    /** `/portal/<slug>`, the personal sign-in address of the new client. */
    portalPath: z.string(),
    verification: clientApplicationVerificationSchema,
});
export type ClientApplicationSubmitResponse = z.infer<typeof clientApplicationSubmitResponseSchema>;

/**
 * The draft as the applicant journey reads it.
 *
 * `resumeSectionId` is DERIVED from the answers and no column holds it, so a
 * cleared answer can never leave a stale resume point behind (SEP-263.8).
 */
export const clientApplicationDraftSchema = z.object({
    application: clientApplicationSummarySchema,
    answers: clientApplicationAnswersSchema,
    /** The section the journey opens on when the client comes back. */
    resumeSectionId: z.string(),
    progress: z.array(clientApplicationSectionProgressSchema),
    /**
     * The form that the answers select (SEP-293). It is derived from the
     * answers, as `resumeSectionId` is, so no column holds it.
     */
    formId: z.enum(CLIENT_APPLICATION_FORM_IDS),
    /**
     * The forms that this deployment shows. The journey selects the form
     * from the working answers with the same rule, so a change of the
     * account type changes the questions at once.
     */
    enabledFormIds: z.array(z.enum(CLIENT_APPLICATION_FORM_IDS)),
    /**
     * The receipt of the submission, or null while the application is a
     * draft. A client who signs in again after they submitted gets the
     * acknowledgement from it, and not the form, because the server refuses
     * each save of a submitted application.
     */
    receipt: clientApplicationSubmitResponseSchema.nullable(),
});
export type ClientApplicationDraft = z.infer<typeof clientApplicationDraftSchema>;

// ---------------------------------------------------------------------------
// The documents of the pack, before the submission (SEP-293 section 9)
// ---------------------------------------------------------------------------

/**
 * Where the client stands with one document of the pack on the application.
 * The application has no `superseded` state: the pack is the live library.
 */
export const applicationDocumentStatusSchema = z.enum(['not_opened', 'opened', 'acknowledged']);
export type ApplicationDocumentStatus = z.infer<typeof applicationDocumentStatusSchema>;

/**
 * One document that the client must open and acknowledge before they submit.
 *
 * The service type that the client chose selects the pack: each live file of
 * the firm library that the firm gives at the application for that service.
 * `id` is the id of that file (the `FirmDocument` row), so a new file of the
 * same document is a new item, and the client acknowledges it again.
 */
export const applicationDocumentSchema = z.object({
    id: z.string(),
    documentCode: z.string(),
    title: z.string(),
    versionLabel: z.string().nullable(),
    status: applicationDocumentStatusSchema,
    openedAt: z.string().nullable(),
    acknowledgedAt: z.string().nullable(),
});
export type ApplicationDocument = z.infer<typeof applicationDocumentSchema>;

/** The answer of `GET /api/apply/documents`. */
export const applicationDocumentsResponseSchema = z.object({
    /**
     * False when this deployment does not ask for the acknowledgement, or the
     * client has not chosen a service type yet. The list is then empty.
     */
    requested: z.boolean(),
    documents: z.array(applicationDocumentSchema),
    /** True when each document is acknowledged. The submission asks this. */
    complete: z.boolean(),
});
export type ApplicationDocumentsResponse = z.infer<typeof applicationDocumentsResponseSchema>;

/** The body of `POST /api/apply/documents/:documentId/acknowledge`. */
export const acknowledgeApplicationDocumentRequestSchema = z.object({
    statementCode: portalAcknowledgementStatementCodeSchema,
});

/** A submission the server refused, with the sections that are not finished. */
export const clientApplicationSubmitRefusalSchema = z.object({
    error: z.string(),
    code: z.string().optional(),
    fieldErrors: z.array(clientApplicationFieldErrorSchema).optional(),
});

export const clientApplicationContract = c.router(
    {
        listInvestmentManagers: {
            method: 'GET',
            path: '/apply/investment-managers',
            responses: { 200: applyInvestmentManagersResponseSchema },
            summary: 'The investment managers a visitor can propose',
        },
        start: {
            method: 'POST',
            path: '/apply/start',
            body: applyStartRequestSchema,
            responses: {
                200: clientApplicationSessionResponseSchema,
                // 400 = the selector names no eligible manager.
                400: errorBody,
                // 409 = an application already exists for this email address.
                409: errorBody,
            },
            summary: 'Create the applicant account and the draft application',
        },
        describeInvitation: {
            method: 'POST',
            path: '/apply/invitation/describe',
            body: applyInvitationDescribeRequestSchema,
            responses: {
                200: applyInvitationResponseSchema,
                // 404 = the token names no open invitation: it is wrong, used
                // or expired. The three answer alike.
                404: errorBody,
            },
            summary: 'The email address and the forenames of one open invitation (SEP-289)',
        },
        acceptInvitation: {
            method: 'POST',
            path: '/apply/invitation',
            body: applyAcceptInvitationRequestSchema,
            responses: {
                200: clientApplicationSessionResponseSchema,
                404: errorBody,
                // 409 = an application already exists for this email address.
                409: errorBody,
            },
            summary:
                'Accept an invitation: set the password, and open the application that the invitation binds to a case (SEP-289)',
        },
        signIn: {
            method: 'POST',
            path: '/apply/session',
            body: applySignInRequestSchema,
            responses: {
                200: clientApplicationSessionResponseSchema,
                401: errorBody,
            },
            summary: 'Sign an applicant in to their own application',
        },
        getSession: {
            method: 'GET',
            path: '/apply/session',
            responses: { 200: clientApplicationSessionResponseSchema },
            summary: 'The application behind the applicant cookie, or null',
        },
        signOut: {
            method: 'POST',
            path: '/apply/sign-out',
            body: z.object({}),
            responses: { 200: z.object({ ok: z.literal(true) }) },
            summary: 'End the applicant session',
        },
        getDraft: {
            method: 'GET',
            path: '/apply/draft',
            responses: {
                200: clientApplicationDraftSchema,
                // 401 = no live applicant session. The page sends the visitor
                // back to the start page to sign in.
                401: errorBody,
            },
            summary: 'The draft behind the applicant cookie, and where to resume',
        },
        saveDraft: {
            method: 'POST',
            path: '/apply/draft',
            body: applySaveDraftRequestSchema,
            responses: {
                200: clientApplicationDraftSchema,
                // 400 = an answer does not match its question. The draft is
                // unchanged and `fieldErrors` names each field.
                400: applyDraftRefusalSchema,
                401: errorBody,
                // 409 = the application is submitted (`code` is
                // `application_submitted`), so no save changes it. The page
                // then reads the draft again, and its `receipt` opens the
                // acknowledgement.
                409: errorBody,
            },
            summary: 'Merge answers into the draft behind the applicant cookie',
        },
        getVerification: {
            method: 'GET',
            path: '/apply/verification',
            responses: {
                200: clientApplicationVerificationSchema,
                401: errorBody,
            },
            summary: 'Where the identity check of this application stands',
        },
        submit: {
            method: 'POST',
            path: '/apply/submit',
            body: z.object({}),
            responses: {
                200: clientApplicationSubmitResponseSchema,
                401: errorBody,
                // 409 = a section is not finished, or a document of the pack
                // is not acknowledged (`documents_unacknowledged`). The
                // identity check is not a gate: the server starts it after
                // the submission (SEP-278).
                409: clientApplicationSubmitRefusalSchema,
            },
            summary: 'Submit the application: one atomic act (decision D10)',
        },
        // SEP-293 section 9. The client opens and acknowledges each document
        // of the pack of their service type before they submit.
        listDocuments: {
            method: 'GET',
            path: '/apply/documents',
            responses: {
                200: applicationDocumentsResponseSchema,
                401: errorBody,
            },
            summary:
                'The documents of the pack of the chosen service type, and where the client stands with each one',
        },
        openDocument: {
            method: 'POST',
            path: '/apply/documents/:documentId/open',
            body: z.object({}),
            responses: {
                200: applicationDocumentsResponseSchema,
                401: errorBody,
                // 404 = the document is not in the pack of this application.
                404: errorBody,
                409: errorBody,
            },
            summary: 'Record the first open of one document of the pack',
        },
        acknowledgeDocument: {
            method: 'POST',
            path: '/apply/documents/:documentId/acknowledge',
            body: acknowledgeApplicationDocumentRequestSchema,
            responses: {
                200: applicationDocumentsResponseSchema,
                401: errorBody,
                404: errorBody,
                // 409 `not_opened` = the client did not open the document.
                // 409 `application_submitted` = the application is locked.
                409: errorBody,
            },
            summary: 'Record that the client read and understood one document of the pack',
        },
        addressAvailability: {
            method: 'GET',
            path: '/apply/address/availability',
            responses: {
                200: addressLookupAvailabilitySchema,
                401: errorBody,
            },
            summary: 'Whether this deployment can look an address up by postcode',
        },
        addressLookup: {
            method: 'GET',
            path: '/apply/address/lookup',
            query: z.object({ postcode: z.string() }),
            responses: {
                200: addressLookupResponseSchema,
                // 400 = the postcode is not a UK postcode shape.
                400: errorBody,
                401: errorBody,
                // 502 = the vendor answered, and the answer was not usable.
                502: errorBody,
                // 503 = this deployment holds no vendor key, so the journey
                // shows the manual address fields alone and invents no address
                // (decision D7).
                503: errorBody,
            },
            summary: 'The addresses at one UK postcode, for a signed-in applicant',
        },
        addressSearch: {
            method: 'GET',
            path: '/apply/address/search',
            query: z.object({ q: z.string() }),
            responses: {
                200: addressLookupResponseSchema,
                400: errorBody,
                401: errorBody,
                502: errorBody,
                503: errorBody,
            },
            summary: 'The addresses that match a free-text term, for a signed-in applicant',
        },
        addressResolve: {
            method: 'GET',
            path: '/apply/address/resolve',
            query: z.object({ id: z.string() }),
            responses: {
                200: addressLookupResultSchema,
                400: errorBody,
                401: errorBody,
                // 404 = the vendor does not hold that suggestion.
                404: errorBody,
                502: errorBody,
                503: errorBody,
            },
            summary: 'The lines of one suggestion, for a signed-in applicant',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
